"use client";

import { useLayoutEffect } from "react";
import { useSelectedLayoutSegment } from "next/navigation";
import { sectionScrollState } from "./sectionScrollState";

/**
 * Pins the reader to the top of the page on every navigation into or
 * within a section layout, overriding Next.js's App Router scroll
 * handler.
 *
 * Why this is needed: Next.js's layout-router scrolls the new *page*'s
 * first DOM node into view (`node_modules/next/dist/client/components/
 * layout-router.js` — `topOfElementInViewport` / `scrollIntoView`). When
 * the section layout's chrome (masthead + sticky tab strip) exceeds the
 * viewport — which it does on `/water` and `/climate` at current hero
 * heights — the handler scrolls past the masthead, landing the reader
 * on the figure grid. The sentinel-based fix that would normally pin
 * the scroll target at document top doesn't survive `RouteFade`'s
 * `transform`-based animation (transform creates a containing block, so
 * an absolutely-positioned `top: 0` resolves below the hero, not at the
 * page top).
 *
 * Placement: render this as the *last* child of the section layout, as
 * a sibling that comes after `RouteFade`. React fires `useLayoutEffect`
 * bottom-up and in sibling order, so this effect runs after the
 * page-level layout-router's scroll has already executed during commit
 * — letting us synchronously override it before paint.
 *
 * Carve-outs:
 *   - tab clicks: `SectionTabs` sets `sectionScrollState.tabClicked` so
 *     its own scroll-to-flush wins instead.
 *   - hash navigations: defer to Next.js so `/water#anchor` lands on
 *     the anchor, not the masthead.
 */
export function RouteScrollTop() {
  const segment = useSelectedLayoutSegment();
  useLayoutEffect(() => {
    if (sectionScrollState.tabClicked) return;
    if (window.location.hash) return;
    window.scrollTo(0, 0);
  }, [segment]);
  return null;
}
